import ActivityAppState from '../apps/activities/state'
import ReportAppState from '../apps/reports/state'
import SettingsAppState from '../apps/settings/state'
import SlidewareAppState from '../apps/slideware/state'
import getStorage from '../storage/index'

function resetLocalData () {
  this.set({
    settings: getStorage(this.get('user'), 'setting')
  })

  // Propagate theme change event
  this.get('settings').on('change:theme', (model, theme) => {
    this.trigger('change:theme', model, theme)
  })

  this.trigger('change:theme', this.get('settings'), this.get('settings').get('theme'))
}

/**
 * Tracks application state
 *
 * @event change:theme Theme change
 *  @property {String} name Theme name
 *
 * @event session:start User logged in and model hydrated
 *  @property {Model} user User model
 *
 * @event session:logout User logged out
 * @event session:anonymous No user on startup
 *
 */
export default class State extends Backbone.Model {
  get defaults () {
    return {
      role: 'presenter',
      page: null,
      settings: null,
      user: null,
      participant: null,
      session: null,
      externalScreenConnected: false,
      mode: 'standalone',
      // general app ready state
      ready: false
    }
  }

  get defaultPage () {
    return 'activities'
  }

  get page () {
    return this.get('page')
  }

  get loggedIn () {
    return !!this.get('user').get('id')
  }

  initialize (attrs = {}, options = {}) {
    // Track states for apps
    this.appStates = {
      activities: new ActivityAppState({ user: attrs.user, mode: attrs.mode }),
      reporting: new ReportAppState({ user: attrs.user }),
      settings: new SettingsAppState(),
      slideware: new SlidewareAppState({ user: attrs.user })
    }

    this.on('change:page', (_model, page) => {
      if (page === 'activities') {
        this.appStates.activities.changePage('activities')
      } else if (page === 'reports') {
        this.appStates.reporting.showHome()
      }
    })

    // Session is fully loaded with user
    this.on('session:start', () => {
      resetLocalData.call(this)

      // Session started. Set app to ready state
      if (this.get('participant').get('must_consent')) {
        // check whether we should show the terms page first
        this.set({
          ready: true,
          page: 'terms_agreement'
        })
      } else {
        this.set({ ready: true })
      }
    })

    // Automatically go to the login page if we're initially logged out
    this.on('session:logout session:anonymous', () => {
      // When the page changes, we're assuming the app is ready
      // Session started. Set app to ready state
      this.set({ ready: true })
    })

    // Nicety
    this.get('session').on('session:stopped', () => {
      this.trigger('session:logout')
    })

    this.get('session').on('session:started', () => {
      // Session start could have an anonymous user
      if (this.get('user').id) {
        // if the user is a participant, convert them to presenter
        if (!this.get('user').get('account_id')) {
          Backbone.ajax({
            method: 'POST',
            url: '/api/account'
          }).done((json) => {
            if (json) {
              this.get('user').set({ account: json })
              // must fetch the groups here since fetching them as a participant fails
              this.get('user').groups.fetch()
            }
          })
        // the session doesn't always fetch account data, so do that here
        } else if (this.get('user').get('account') && Object.keys(this.get('user').get('account')).length === 0) {
          this.get('session').start()
        }

        this.trigger('session:start', this.get('user'))
      } else {
        this.trigger('session:anonymous')
      }
    })

    // Propagate attributes
    this.on('change:externalScreenConnected', (model, val) => {
      this.appStates.activities.set({ externalScreenConnected: val })
    })
  }

  changePage (page, options = {}) {
    this.set({ page }, options)
  }

  messageHistory () {
    return getStorage(this.get('user'), 'message')
  }

  notifyPageLoaded (page) {
    if (page === this.page) {
      this.trigger('page:loaded', page)
    }
  }

  inspect () {
    const result = super.toJSON()

    // Deal with state model attributes whose values are complex types
    result.participant = this.get('participant')?.toJSON()
    result.settings = this.get('settings')?.toJSON()
    result.user = this.get('user')?.toJSON().user

    // session only has references to the participant and user, which we otherwise have anyway
    delete result.session

    // Deal with state object properties tacked on outside of the underlying Backbone.Model
    result.appStates = {}
    Object.entries(this.appStates).forEach(([key, value]) => {
      result.appStates[key] = value.inspect()
    })

    return result
  }
}
